import { Color4 } from "@babylonjs/core";
import { useTheme } from "@mui/material";
import * as React from "react";

import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { LayoutItem, ThreeDPanelState } from "@/shared/state/visualization";

import { useWorkspaceTimer } from "../../WorkspaceCtx";
import { DataNotFoundMessage } from "../DataNotFoundMessage";
import { PanelHeader } from "../PanelHeader";
import { PanelLayout } from "../PanelLayout";
import { RenderingError } from "../RenderingError";

import { BabylonManager, BabylonTheme } from "./BabylonManager";
import { DataServerManager } from "./DataServer/DataServerManager";
import styles from "./ThreeDPanel.module.css";

interface ThreeDPanelProps {
  layout: LayoutItem;
  state: ThreeDPanelState;
}

export function ThreeDPanel({ layout, state }: ThreeDPanelProps) {
  const theme = useBabylonTheme();
  const canvasRef = React.useRef<HTMLCanvasElement>();
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [manager, setManager] = React.useState<BabylonManager | null>(null);
  const timer = useWorkspaceTimer();
  const initTheme = React.useRef(theme);
  const initState = React.useRef(state);
  const initManager = React.useCallback(
    (
      dataServerManager: DataServerManager,
      time: bigint,
      canvas: HTMLCanvasElement,
    ) => {
      try {
        const manager = new BabylonManager({
          dataServer: dataServerManager,
          canvas,
          time,
          initialState: initState.current,
          theme: initTheme.current,
          callbacks: {
            onLoadingStatusChanged: (loadingStatus) => {
              setIsLoading(loadingStatus);
            },
          },
        });
        setManager(manager);
        return manager;
      } catch (e) {
        // This is normally a WebGL Not Supported error, which is unrecoverable.
        ErrorMonitoringService.captureError(e);
        setError(e as Error);
        // Short-circuit because the panel is not viable.
        return;
      }
    },
    [setIsLoading, setManager, setError],
  );

  React.useEffect(
    function onStateUpdated() {
      try {
        manager?.setState(state);
      } catch (e) {
        ErrorMonitoringService.captureError(e);
      }
    },
    [state, manager],
  );

  React.useEffect(
    function updateTheme() {
      manager?.setTheme(theme);
    },
    [theme, manager],
  );

  const [dataClient, setDataClient] = React.useState<DataServerManager | null>(
    null,
  );
  React.useEffect(
    function initDataServer() {
      const controller = new AbortController();
      DataServerManager.create(controller)
        .then((dsm) => {
          setDataClient(dsm);
        })
        .catch((e) => {
          if (!controller.signal.aborted && !isAbortError(e)) {
            ErrorMonitoringService.captureError(e);
          }
        });
      return () => {
        controller.abort();
      };
    },
    [setDataClient],
  );

  React.useEffect(
    function init() {
      if (canvasRef.current === undefined || dataClient === null) {
        return;
      }
      const canvas = canvasRef.current;

      // Absorb canvas mouse scrolls.
      function absorbMouseWheelEvents(event: MouseEvent) {
        event.preventDefault();
        return false;
      }

      canvas.addEventListener("wheel", absorbMouseWheelEvents, false);
      canvas.addEventListener("drag", absorbMouseWheelEvents, false);
      canvas.addEventListener("click", absorbMouseWheelEvents, false);

      const manager = initManager(dataClient, timer.currentTime, canvas);
      const tickListener = manager
        ? timer.addListener("tick", function onTick(timer) {
            manager.setTime(timer.currentTime);
          })
        : undefined;
      const seekListener = manager
        ? timer.addListener("seek", function onTick(timer) {
            manager.setTime(timer.currentTime);
          })
        : undefined;
      return () => {
        setManager(null);
        setDataClient(null);
        manager?.dispose();

        canvas.removeEventListener("wheel", absorbMouseWheelEvents);
        canvas.removeEventListener("drag", absorbMouseWheelEvents);
        canvas.removeEventListener("click", absorbMouseWheelEvents);
        if (tickListener) {
          timer.removeListener(tickListener);
        }
        if (seekListener) {
          timer.removeListener(seekListener);
        }
      };
    },
    [dataClient, initManager, setManager, timer],
  );

  return (
    <PanelLayout
      header={<PanelHeader className={styles.panelHeader} state={state} />}
      isLoading={isLoading && !error}
      layout={layout}
      state={state}
    >
      {!error && (
        <>
          <canvas
            className={styles.canvas}
            ref={(ref) => (canvasRef.current = ref ?? undefined)}
          />
        </>
      )}
      {/* TODO(dave): enable onClearError in cases where the error is recoverable. */}
      {error && <RenderingError error={error} />}
      <DataNotFoundMessage panelId={state.id} />
    </PanelLayout>
  );
}

function useBabylonTheme(): BabylonTheme {
  const theme = useTheme();
  const babylonTheme = React.useMemo(() => {
    // This logic would use the theme directly, but babylon is very particular about
    // the hex strings it desires (no shorthand like #FFF etc that CSS is OK with),
    // so configuring it explicitly here instead.
    const backgroundColor =
      theme.palette.mode === "dark"
        ? Color4.FromHexString("#323236FF")
        : Color4.FromHexString("#FFFFFFFF");
    return {
      backgroundColor,
    };
  }, [theme]);
  return babylonTheme;
}
